/**
 * DesktopView — the workspace as a desktop: icons you arrange over the real
 * Vault documents and folders, folders that open into windows you can move,
 * resize and navigate, files that open the preferred way, and a Trash.
 *
 * Two things never get confused here:
 *   - ARRANGEMENT — where this person put an icon, their windows, and how
 *     each folder is viewed (icon size / list, sort). Saved per person on the
 *     server (`useDesktopLayout`); any role may arrange, in any area, because
 *     it changes nothing anyone else sees.
 *   - MEMBERSHIP — which folder a file is in. Changed only through the
 *     placement controller every view shares (`useDocumentPlacement`), so a
 *     drop here means exactly what a drop in Browse means: a move (or, for an
 *     email attachment, a copy), or a refusal that says why. A drop never
 *     grants access and never changes a file's visibility. Per-file actions
 *     come from `useFileActions`, the same model Browse renders.
 *
 * A cross-container drop is NOT optimistic: the icons stay where they were,
 * drawn as pending, until the server answers; a position is written in the
 * destination only for files that actually moved.
 *
 * Windows (`useDesktopWindows`): a folder window navigates in place when a
 * folder inside it is opened — Back / Forward / Up — and "Open in new
 * window" makes another. Files open in viewer windows (or straight in an
 * arche, when that is where the kind belongs — lib/file-openers.ts).
 *
 * Style (lib/desktop-style.ts): Windows (taskbar, Trash on the desktop,
 * Explorer-style windows) or Mac (menu bar, Dock with the Trash, Finder-style
 * windows with a sidebar and Columns / Gallery views).
 *
 * Below `md` there are no floating windows: one full-width canvas with a
 * breadcrumb, and opening a folder navigates into it.
 */
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import {
  Download,
  FolderClosed,
  HardDrive,
  Info,
  PackageOpen,
  Settings2,
  Trash2,
} from 'lucide-react';
import { api, type VaultDocumentDto, type VaultDocumentFolderDto } from '@/lib/api';
import type { HolderSwitcherOption } from '@/components/HolderSwitcher';
import { useGlobalFiasAIOptional } from '@/components/fias-ai/hooks/GlobalFiasAIContext';
import { iconForMime } from '@/components/files/file-dialog/format';
import {
  EMAIL_ATTACHMENTS_PATH,
  archeNamespaceOfPath,
  isFolderPathAtOrUnder,
} from '@/lib/vault-areas';
import { cn } from '@/lib/utils';
import {
  useCreateVaultDocumentFolder,
  useDeleteVaultDocumentFolder,
  useEnsureVaultSystemFolders,
  useMoveVaultDocumentFolder,
  useRenameVaultDocumentFolder,
  useUploadVaultDocument,
  useVaultDocumentFolders,
  useVaultDocumentsList,
} from '../../hooks/useVaultDocuments';
import { useFileActions } from '../../hooks/useFileActions';
import { useDesktopLayout } from '../../hooks/useDesktopLayout';
import { archeNameFor, useDocumentPlacement } from '../DocumentPlacement';
import { FileViewer } from '../shared/FileViewer';
import { FilePreviewPane } from '../shared/FilePreviewPane';
import { DeleteFolderDialog } from '../shared/DeleteFolderDialog';
import { EmptyTrashDialog } from '../shared/TrashDialogs';
import { NoticeBanner, type Notice } from '../shared/NoticeBanner';
import { MAX_UPLOAD_BYTES, friendlyUploadError } from '../DocumentUploader';
import { canChangeMembership } from '../../lib/file-capabilities';
import { canMoveFolderInto, friendlyFolderError } from '../../lib/folder-moves';
import { downloadVaultDocument } from '../../lib/download';
import { runBounded } from '../../lib/run-bounded';
import {
  DESKTOP_VIEW_KEY,
  ROOT_CONTAINER,
  TRASH_KEY,
  cellIndex,
  compareArrangeOrder,
  emptyPatch,
  nearestFreeCell,
  positionKey,
  type Cell,
  type FolderView,
  type SortKey,
} from '../../lib/desktop-layout';
import {
  DEFAULT_FOLDER_VIEW,
  DEFAULT_TRASH_VIEW,
  chooseSort,
  effectiveMode,
  metricsFor,
  type DesktopStyle,
} from '../../lib/folder-view';
import { useDesktopStyle } from '../../lib/desktop-style';
import type { FileApp } from '../../lib/file-openers';
import { useFileOpeners } from '../../hooks/useFileOpeners';
import { noteUserChoseApp } from '@/components/fias-ai/plugin-handoff-consent';
import {
  DesktopContext,
  type CanvasInfo,
  type DesktopContextValue,
  type DesktopSelection,
  type MenuRequest,
} from './desktop-context';
import {
  areaOfContainer,
  columnsFor,
  containerOfFolderId,
  desktopSlot,
  folderIdOfContainer,
  rowsFor,
  takenCells,
  type DesktopItem,
} from './desktop-model';
import {
  useCanvasDrag,
  type DragSession,
  type DropEvaluation,
  type DropTarget,
} from './useCanvasDrag';
import { useDesktopWindows } from './useDesktopWindows';
import { DesktopCanvas } from './DesktopCanvas';
import { FolderWindow } from './FolderWindow';
import { WindowStatusBar, WindowToolbar, type PathPart } from './WindowToolbar';
import { ColumnsView, GalleryView } from './MacViews';
import { MacDock, MacMenuBar, SidebarHeading, SidebarItem } from './MacChrome';
import { DesktopTaskbar, type TaskbarWindow } from './DesktopTaskbar';
import { DesktopContextMenu } from './DesktopContextMenu';
import { DesktopSettingsDialog } from './DesktopSettingsDialog';
import { contextMenuEntries, macMenuBar, type MenuActions } from './desktop-menus';
import { TrashWindowContent } from './TrashWindowContent';
import { NarrowDesktop } from './NarrowDesktop';
import { DragGhost } from './DragGhost';
import { useElementSize, useMediaQuery } from './dom-hooks';

/**
 * What the workspace (and Fias AI's on-screen actions) can ask of the
 * Desktop. Ids only in the snapshot — it may be read into the assistant's
 * context, and names can come from anyone.
 */
export interface DesktopHandle {
  openFolder: (container: string, options?: { newWindow?: boolean }) => void;
  openFile: (documentId: string) => Promise<void>;
  reveal: (ids: { documentIds?: string[]; folderIds?: string[] }) => Promise<void>;
  setFolderView: (container: string, patch: Partial<FolderView>) => void;
  snapshot: () => {
    openFolderId: string | null;
    openFolderIds: string[];
    selectedDocumentIds: string[];
    selectedFolderIds: string[];
    viewingDocumentId: string | null;
  };
}

export interface DesktopViewProps {
  holderId: string;
  holder: HolderSwitcherOption | null | undefined;
  /** A deep-linked folder to open once (its window, or navigate into it). */
  revealFolderPath: string | null;
  onRevealed: () => void;
  handleRef?: Ref<DesktopHandle>;
}

/** Uploads in flight at once for a drop of several files. */
const UPLOAD_CONCURRENCY = 2;

const DEFAULT_DESKTOP_VIEW: FolderView = { mode: 'icons-md', sort: 'name', dir: 'asc' };

const AREA_NOTES = {
  'arche-saves':
    'Arche Saves holds files your arches delivered. You can open, rename, delete or move them into your Documents, but you can’t add files here.',
  'my-fias':
    'My-Fias holds the files your arches keep for themselves. You can look at them and delete them, but you can’t move or add files here.',
} as const;

/** The note above a folder window: its area's, or Email Attachments' own. */
function areaNoteFor(folder: VaultDocumentFolderDto | undefined, area: 'arche-saves' | 'my-fias') {
  if (folder && isFolderPathAtOrUnder(folder.path, EMAIL_ATTACHMENTS_PATH)) {
    return 'Email attachments stay with their email. Dragging one into your Documents makes a copy that’s yours to keep.';
  }
  return AREA_NOTES[area];
}

function windowIcon(container: string, folder: VaultDocumentFolderDto | undefined): ReactNode {
  if (container === TRASH_KEY)
    return <Trash2 className="h-4 w-4 text-muted-foreground" aria-hidden />;
  if (container === ROOT_CONTAINER)
    return <HardDrive className="h-4 w-4 text-primary/80" aria-hidden />;
  if (folder?.path === '/Arche Saves')
    return <PackageOpen className="h-4 w-4 text-primary" aria-hidden />;
  if (folder?.path === '/My-Fias')
    return <Settings2 className="h-4 w-4 text-muted-foreground" aria-hidden />;
  return <FolderClosed className="h-4 w-4 text-primary/80" aria-hidden />;
}

/** "New folder", or "New folder (2)" … — the first name no sibling uses. */
function freshFolderName(siblings: readonly VaultDocumentFolderDto[]): string {
  const taken = new Set(siblings.map((f) => f.name.toLowerCase()));
  if (!taken.has('new folder')) return 'New folder';
  for (let n = 2; n < 1000; n += 1) {
    const name = `New folder (${n})`;
    if (!taken.has(name.toLowerCase())) return name;
  }
  return `New folder (${Date.now()})`;
}

export function DesktopView({
  holderId,
  holder,
  revealFolderPath,
  onRevealed,
  handleRef,
}: DesktopViewProps) {
  const foldersQuery = useVaultDocumentFolders(holderId);
  const folders = useMemo(() => foldersQuery.data ?? [], [foldersQuery.data]);
  const foldersById = useMemo(() => new Map(folders.map((f) => [f.folderId, f])), [folders]);
  const placement = useDocumentPlacement({ folders });
  const fileActions = useFileActions({ holderId, holder, folders, placement });
  const layout = useDesktopLayout(holderId);
  const trashList = useVaultDocumentsList(holderId, { deletedOnly: true, includeArchived: true });
  const trashFull = trashList.total > 0;
  const canWrite = canChangeMembership(fileActions.actor);
  const wide = useMediaQuery('(min-width: 768px)');
  const [style, setStyle] = useDesktopStyle();
  const mac = style === 'mac';
  const ai = useGlobalFiasAIOptional();

  const moveFolder = useMoveVaultDocumentFolder();
  const createFolder = useCreateVaultDocumentFolder();
  const renameFolder = useRenameVaultDocumentFolder();
  const deleteFolder = useDeleteVaultDocumentFolder();
  const upload = useUploadVaultDocument();

  // Holders provisioned before the folder substrate have no system folders;
  // Browse heals them on first visit, and so must the Desktop (it is the
  // default view, so Browse may never mount).
  const ensureSystemFolders = useEnsureVaultSystemFolders();
  const ensureSystemFoldersMutate = ensureSystemFolders.mutate;
  const ensured = useRef(false);
  useEffect(() => {
    if (ensured.current || foldersQuery.isLoading || foldersQuery.isError) return;
    if ((foldersQuery.data ?? []).length > 0) return;
    ensured.current = true;
    ensureSystemFoldersMutate(holderId);
  }, [
    holderId,
    foldersQuery.isLoading,
    foldersQuery.isError,
    foldersQuery.data,
    ensureSystemFoldersMutate,
  ]);

  const [notice, setNotice] = useState<Notice | null>(null);
  const [selection, setSelection] = useState<DesktopSelection>({
    container: ROOT_CONTAINER,
    keys: new Set(),
  });
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [clipboard, setClipboard] = useState<(DesktopSelection & { items: DesktopItem[] }) | null>(
    null,
  );
  const [renaming, setRenaming] = useState<{ container: string; key: string } | null>(null);
  const [menu, setMenu] = useState<MenuRequest | null>(null);
  /** Narrow screens (no windows) and Quick Look use the full-screen viewer. */
  const [viewerDoc, setViewerDoc] = useState<VaultDocumentDto | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<VaultDocumentFolderDto | null>(null);
  const [deleteFolderError, setDeleteFolderError] = useState<string | null>(null);
  const [emptyTrash, setEmptyTrash] = useState<{ count: number } | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tidyVersion, setTidyVersion] = useState(0);
  const [narrowContainer, setNarrowContainer] = useState<string>(ROOT_CONTAINER);
  const registry = useRef(new Map<string, CanvasInfo>());
  const uploadTarget = useRef<{ container: string; cell: Cell | null } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const areaSize = useElementSize(areaRef, { width: 1200, height: 700 });

  const { enqueue } = layout;
  const exists = useCallback(
    (container: string) =>
      container === ROOT_CONTAINER || container === TRASH_KEY || foldersById.has(container),
    [foldersById],
  );
  const win = useDesktopWindows({ layout, areaSize, exists });
  const { windows } = win;

  // --- Views ------------------------------------------------------------------
  const storedViews = layout.layout.views;
  const viewOf = useCallback(
    (container: string): FolderView =>
      storedViews[container] ??
      (container === TRASH_KEY ? DEFAULT_TRASH_VIEW : DEFAULT_FOLDER_VIEW),
    [storedViews],
  );
  const desktopView = storedViews[DESKTOP_VIEW_KEY] ?? DEFAULT_DESKTOP_VIEW;
  const setView = useCallback(
    (container: string, view: FolderView) => {
      const patch = emptyPatch();
      patch.views.set(container, view);
      enqueue(patch);
    },
    [enqueue],
  );
  /**
   * Show `next` in window `windowId`. A folder with no view of its own takes
   * the window's current one — browsing down from a folder in Columns (or
   * Details) stays in Columns, as Finder and Explorer both keep a window's view
   * while you move through it. The Trash keeps its own.
   */
  const { navigate: navigateTo } = win;
  const navigateWindow = useCallback(
    (windowId: string, next: string) => {
      const current = windows[windowId]?.folder;
      if (current && next !== TRASH_KEY && current !== TRASH_KEY && !storedViews[next]) {
        const inherited = storedViews[current];
        if (inherited) setView(next, inherited);
      }
      navigateTo(windowId, next);
    },
    [navigateTo, setView, storedViews, windows],
  );

  // --- Arrangement ------------------------------------------------------------
  const arrange = useCallback<DesktopContextValue['arrange']>(
    (container, entries) => {
      if (entries.length === 0) return;
      const patch = emptyPatch();
      for (const [key, cell] of entries) patch.positions.set(positionKey(container, key), cell);
      enqueue(patch);
    },
    [enqueue],
  );
  const forget = useCallback<DesktopContextValue['forget']>(
    (container, keys) => {
      if (keys.length === 0) return;
      const patch = emptyPatch();
      for (const key of keys) patch.positions.set(positionKey(container, key), null);
      enqueue(patch);
    },
    [enqueue],
  );
  // The registry is a ref (drops and paste read it); what's DRAWN from it —
  // the item counts in the status bars and taskbar — re-renders only when a
  // container's counts actually change.
  const [, setRegistryVersion] = useState(0);
  const registerCanvas = useCallback<DesktopContextValue['registerCanvas']>((canvas, info) => {
    const before = registry.current.get(canvas);
    if (info) registry.current.set(canvas, info);
    else registry.current.delete(canvas);
    const counts = (i?: CanvasInfo) =>
      i ? `${i.items.length}|${i.loadedFiles}|${i.totalFiles}` : '';
    if (counts(before) !== counts(info ?? undefined)) setRegistryVersion((v) => v + 1);
  }, []);

  /** Cells for `items` starting at `start`, keeping their relative offsets,
   *  each moved to the nearest free cell of `container`. Only the desktop
   *  (a free canvas) has cells; a folder window flows by its view. */
  const placeAt = useCallback(
    (
      container: string,
      items: readonly { key: string; offset: { dc: number; dr: number } }[],
      start: Cell,
    ): Array<[string, Cell]> => {
      // The desktop registers under its container; windows under their ids —
      // so this finds a FREE canvas, or nothing.
      const info = registry.current.get(container);
      if (!info?.free) return [];
      const columns = info?.columns ?? 8;
      const taken = info
        ? takenCells(info.positions, columns, new Set(items.map((i) => i.key)))
        : new Set<number>();
      return items.map(({ key, offset }) => {
        const cell = nearestFreeCell(
          { c: start.c + offset.dc, r: start.r + offset.dr },
          taken,
          columns,
        );
        taken.add(cellIndex(cell, columns));
        return [key, cell];
      });
    },
    [],
  );

  const tidy = useCallback(
    (container: string) => {
      const stored = layout.layout.containers[container];
      if (stored) forget(container, Object.keys(stored));
      setTidyVersion((v) => v + 1);
    },
    [forget, layout.layout.containers],
  );
  const resetArrangement = useCallback(() => {
    const patch = emptyPatch();
    for (const [container, items] of Object.entries(layout.layout.containers)) {
      for (const key of Object.keys(items)) patch.positions.set(positionKey(container, key), null);
    }
    enqueue(patch);
    setTidyVersion((v) => v + 1);
  }, [enqueue, layout.layout.containers]);

  /**
   * "Sort by" on the desktop: arrange the icons once in that order (Windows
   * does this; the icons stay free to move afterwards). Folders first, then
   * files, in the chosen order, into the style's slots.
   */
  const sortDesktop = useCallback(
    (sort: SortKey) => {
      const info = registry.current.get(ROOT_CONTAINER);
      if (!info) return;
      const rank = (item: DesktopItem) =>
        item.kind === 'trash' || (item.kind === 'folder' && item.system)
          ? 0
          : item.kind === 'folder'
            ? 1
            : 2;
      const value = (item: DesktopItem): string | number => {
        if (item.kind !== 'file') return item.name.toLowerCase();
        if (sort === 'size') return -item.doc.sizeBytes;
        if (sort === 'modified') return -Date.parse(item.doc.updatedAt);
        if (sort === 'type') return `${item.doc.mimeType ?? ''}|${item.name.toLowerCase()}`;
        return item.name.toLowerCase();
      };
      const ordered = [...info.items].sort((a, b) => {
        if (rank(a) !== rank(b)) return rank(a) - rank(b);
        const va = value(a);
        const vb = value(b);
        if (va !== vb) return va < vb ? -1 : 1;
        return compareArrangeOrder(
          { rank: 0, name: a.name, key: a.key },
          { rank: 0, name: b.name, key: b.key },
        );
      });
      const metrics = metricsFor(desktopView.mode, style);
      const g = { cellW: metrics.cellW, cellH: metrics.cellH, pad: metrics.pad };
      const columns = columnsFor(areaSize.width, g);
      const rows = rowsFor(areaSize.height, g);
      const slot = desktopSlot(style, columns, rows);
      arrange(
        ROOT_CONTAINER,
        ordered.map((item, index) => [item.key, slot(index)] as [string, Cell]),
      );
      setView(DESKTOP_VIEW_KEY, { ...desktopView, sort });
      setTidyVersion((v) => v + 1);
    },
    [areaSize, arrange, desktopView, setView, style],
  );

  // --- Selection -----------------------------------------------------------------
  const select = useCallback<DesktopContextValue['select']>((container, keys, mode) => {
    setSelection((prev) => {
      const next =
        prev.container === container && mode !== 'replace' ? new Set(prev.keys) : new Set<string>();
      for (const key of keys) {
        if (mode === 'toggle' && next.has(key)) next.delete(key);
        else next.add(key);
      }
      return { container, keys: next };
    });
  }, []);
  const markPending = useCallback((keys: readonly string[], on: boolean) => {
    setPending((prev) => {
      const next = new Set(prev);
      for (const key of keys) {
        if (on) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  }, []);

  // --- Opening things ---------------------------------------------------------------
  /** "Open in…" choices, including approved plugin apps (ADR-030). */
  const openers = useFileOpeners();
  /** Open an arche on a file (the platform handoff: it opens as a copy the
   *  arche can save back). */
  const openInApp = useCallback(
    (doc: VaultDocumentDto, app: FileApp) => {
      // The user picked this app for this file: that click is the consent,
      // so a plugin destination opens without a second confirmation (ADR-030).
      noteUserChoseApp(app.archeId, doc.documentId);
      const routed = ai?.openHandoff(
        { kind: 'open_document', documentId: doc.documentId },
        app.archeId,
      );
      if (!routed)
        setNotice({ tone: 'info', message: `${app.label} couldn’t open “${doc.name}”.` });
    },
    [ai],
  );
  /** A file's preview: a viewer window on a wide screen, else full screen. */
  const preview = useCallback(
    (doc: VaultDocumentDto, opener?: { container: string; key: string }) => {
      if (wide) win.openViewer(doc, opener);
      else setViewerDoc(doc);
    },
    [wide, win],
  );
  const openFile = useCallback(
    (doc: VaultDocumentDto, opener?: { container: string; key: string }) => {
      const action = openers.doubleClickAction(doc);
      if (action.kind === 'app') openInApp(doc, action.app);
      else if (action.kind === 'preview') preview(doc, opener);
      else void downloadVaultDocument(doc);
    },
    [openInApp, preview, openers],
  );

  const openItem = useCallback<DesktopContextValue['openItem']>(
    (item, container, options = {}) => {
      if (item.kind === 'file') {
        openFile(item.doc, { container, key: item.key });
        return;
      }
      const target = item.kind === 'trash' ? TRASH_KEY : item.key;
      if (!wide) {
        setNarrowContainer(target);
        return;
      }
      // Inside a window, a folder opens in that window (Explorer and Finder
      // both do this); Ctrl/Cmd, or "Open in new window", makes another.
      if (options.windowId && !options.newWindow && windows[options.windowId]) {
        navigateWindow(options.windowId, target);
        return;
      }
      win.openFolder(target, {
        newWindow: options.newWindow,
        opener: { container, key: item.key },
      });
    },
    [navigateWindow, openFile, wide, win, windows],
  );

  const quickLook = useCallback<DesktopContextValue['quickLook']>((item) => {
    if (item.kind === 'file') setViewerDoc(item.doc);
  }, []);

  // A deep-linked folder, once the folders and the arrangement are in —
  // opened once per link.
  const revealed = useRef<string | null>(null);
  useEffect(() => {
    if (!revealFolderPath || revealed.current === revealFolderPath) return;
    if (!foldersQuery.data || !layout.ready) return;
    revealed.current = revealFolderPath;
    const folder = foldersQuery.data.find((f) => f.path === revealFolderPath);
    if (folder) {
      if (wide) win.openFolder(folder.folderId);
      else setNarrowContainer(folder.folderId);
    }
    onRevealed();
  }, [revealFolderPath, foldersQuery.data, layout.ready, wide, win, onRevealed]);

  // --- Dropping -----------------------------------------------------------------
  const describeViewer = 'Viewers can look at files but not change them.';

  const evaluate = useCallback(
    (
      session: Pick<DragSession, 'container' | 'items'>,
      target: DropTarget | null,
    ): DropEvaluation => {
      if (!target) return { allowed: false, reason: null };
      const { container: origin, items } = session;
      if (target.kind === 'container' && target.container === origin) {
        return { allowed: true, reason: null };
      }
      if (target.kind === 'folder' && items.some((i) => i.key === target.folderId)) {
        return { allowed: false, reason: null };
      }
      if (!canWrite) return { allowed: false, reason: describeViewer };
      if (target.kind === 'trash') {
        const anyTrashable = items.some((i) =>
          i.kind === 'file'
            ? fileActions.capabilitiesOf(i.doc).trash
            : i.kind === 'folder'
              ? !i.folder.isProtected
              : false,
        );
        return anyTrashable
          ? { allowed: true, reason: null }
          : {
              allowed: false,
              reason: items.some((i) => i.kind === 'file')
                ? 'Only workspace admins can delete an arche’s files in a shared workspace.'
                : 'Folders managed by Fias can’t be deleted.',
            };
      }
      const targetFolderId =
        target.kind === 'folder' ? target.folderId : folderIdOfContainer(target.container);
      let anyAllowed = false;
      let reason: string | null = null;
      for (const item of items) {
        if (item.kind === 'file') {
          const { plan, message } = placement.preview(item.doc, targetFolderId);
          if (plan.kind === 'move' || plan.kind === 'copy') anyAllowed = true;
          else if (message) reason ??= message;
        } else if (item.kind === 'folder') {
          if (canMoveFolderInto(foldersById, item.key, targetFolderId)) anyAllowed = true;
          else
            reason ??= item.folder.isProtected
              ? 'Folders managed by Fias can’t be moved.'
              : 'That folder can’t go there.';
        } else {
          reason ??= 'The Trash stays on the desktop.';
        }
      }
      return anyAllowed ? { allowed: true, reason: null } : { allowed: false, reason };
    },
    [canWrite, fileActions, foldersById, placement],
  );

  const trash = useCallback<DesktopContextValue['trash']>(
    (_container, items) => {
      setNotice(null);
      if (!canWrite) {
        setNotice({ tone: 'info', message: describeViewer });
        return;
      }
      const files = items.filter(
        (i): i is Extract<DesktopItem, { kind: 'file' }> => i.kind === 'file',
      );
      const userFolders = items.filter(
        (i): i is Extract<DesktopItem, { kind: 'folder' }> =>
          i.kind === 'folder' && !i.folder.isProtected,
      );
      if (files.length > 0) fileActions.trash(files.map((f) => f.doc));
      if (userFolders.length === 1 && files.length === 0) {
        setDeleteFolderError(null);
        setDeletingFolder(userFolders[0].folder);
      } else if (userFolders.length > 0) {
        setNotice({
          tone: 'info',
          message: 'Folders go to the Trash one at a time — delete a folder on its own.',
        });
      } else if (files.length === 0) {
        setNotice({ tone: 'info', message: 'Folders managed by Fias can’t be deleted.' });
      }
    },
    [canWrite, fileActions],
  );

  const performDrop = useCallback<DesktopContextValue['performDrop']>(
    (session, target) => {
      const { container: origin, items, offsets } = session;
      const offsetOf = (key: string) => offsets.get(key) ?? { dc: 0, dr: 0 };
      if (target.kind === 'trash') {
        trash(origin, items);
        return;
      }
      if (target.kind === 'container' && target.container === origin) {
        // Rearranging: only the desktop keeps positions; a folder window
        // flows by its view, so a drop within it changes nothing.
        if (target.cell && registry.current.get(origin)?.free) {
          arrange(
            origin,
            placeAt(
              origin,
              items.map((i) => ({ key: i.key, offset: offsetOf(i.key) })),
              target.cell,
            ),
          );
        }
        return;
      }
      if (!canWrite) {
        setNotice({ tone: 'info', message: describeViewer });
        return;
      }
      const targetFolderId =
        target.kind === 'folder' ? target.folderId : folderIdOfContainer(target.container);
      const files = items.filter(
        (i): i is Extract<DesktopItem, { kind: 'file' }> => i.kind === 'file',
      );
      const movableFolders = items.filter(
        (i): i is Extract<DesktopItem, { kind: 'folder' }> => i.kind === 'folder',
      );
      const keys = [...files, ...movableFolders].map((i) => i.key);
      markPending(keys, true);
      void (async () => {
        const moved = new Set<string>();
        const copies: VaultDocumentDto[] = [];
        try {
          if (files.length > 0) {
            const results = await placement.placeMany(
              files.map((f) => f.doc),
              targetFolderId,
            );
            for (const [id, result] of results) {
              if (result.outcome === 'moved') moved.add(id);
              else if (result.outcome === 'copied' && result.copy) copies.push(result.copy);
            }
          }
          const folderErrors: string[] = [];
          for (const item of movableFolders) {
            if (!canMoveFolderInto(foldersById, item.key, targetFolderId)) {
              folderErrors.push(
                item.folder.isProtected
                  ? `“${item.name}” is managed by Fias and can’t be moved.`
                  : `“${item.name}” can’t go there.`,
              );
              continue;
            }
            try {
              await moveFolder.mutateAsync({ folderId: item.key, parentFolderId: targetFolderId });
              moved.add(item.key);
            } catch (err) {
              folderErrors.push(`“${item.name}”: ${friendlyFolderError(err, 'move')}`);
            }
          }
          if (folderErrors.length > 0) {
            setNotice({
              tone: 'error',
              message:
                folderErrors.length === 1
                  ? folderErrors[0]
                  : `${folderErrors.length} folders couldn’t be moved.`,
              details: folderErrors.length > 1 ? folderErrors : undefined,
            });
          }
        } finally {
          markPending(keys, false);
        }
        // A copy's original stays where it was; the copy is placed like a
        // moved icon when dropped on the desktop.
        if (copies.length > 0 && target.kind === 'container' && target.cell) {
          arrange(
            target.container,
            placeAt(
              target.container,
              copies.map((doc, i) => ({ key: doc.documentId, offset: { dc: i, dr: 0 } })),
              target.cell,
            ),
          );
        }
        if (moved.size === 0) return;
        const movedItems = items.filter((i) => moved.has(i.key));
        // The icons leave their old container for certain — forget them there…
        forget(
          origin,
          movedItems.map((i) => i.key),
        );
        // …and, when dropped on a canvas, sit where they were let go. Dropped
        // on a folder icon (or pasted), they auto-arrange in the new folder.
        if (target.kind === 'container' && target.cell) {
          arrange(
            target.container,
            placeAt(
              target.container,
              movedItems.map((i) => ({ key: i.key, offset: offsetOf(i.key) })),
              target.cell,
            ),
          );
        }
        setSelection((prev) =>
          prev.container === origin ? { container: origin, keys: new Set() } : prev,
        );
      })();
    },
    [arrange, canWrite, foldersById, forget, markPending, moveFolder, placeAt, placement, trash],
  );

  const drag = useCanvasDrag({
    evaluate,
    drop: performDrop,
    refused: (reason) => setNotice({ tone: 'info', message: reason }),
  });

  // --- Cut / paste --------------------------------------------------------------
  const cut = useCallback<DesktopContextValue['cut']>(
    (container, items) => {
      const movable = items.filter((i) =>
        i.kind === 'file'
          ? fileActions.capabilitiesOf(i.doc).move
          : i.kind === 'folder'
            ? canWrite && !i.folder.isProtected
            : false,
      );
      if (movable.length === 0) {
        const archeFile = items.find(
          (i): i is Extract<DesktopItem, { kind: 'file' }> =>
            i.kind === 'file' && fileActions.areaOf(i.doc) === 'my-fias',
        );
        setNotice({
          tone: 'info',
          message: !canWrite
            ? describeViewer
            : archeFile
              ? `“${archeFile.name}” belongs to ${archeNameFor(
                  archeNamespaceOfPath(
                    archeFile.doc.folderId
                      ? (foldersById.get(archeFile.doc.folderId)?.path ?? null)
                      : null,
                  ),
                  folders,
                )} and can’t be moved.`
              : 'These can’t be moved.',
        });
        return;
      }
      setClipboard({ container, keys: new Set(movable.map((i) => i.key)), items: movable });
    },
    [canWrite, fileActions, folders, foldersById],
  );
  const paste = useCallback<DesktopContextValue['paste']>(
    (container) => {
      if (!clipboard) return;
      if (clipboard.container === container) {
        setClipboard(null);
        return;
      }
      const target: DropTarget = { kind: 'container', container, cell: null };
      const evaluation = evaluate(clipboard, target);
      if (!evaluation.allowed) {
        setNotice({ tone: 'info', message: evaluation.reason ?? 'Those can’t go there.' });
        return;
      }
      performDrop(
        { container: clipboard.container, items: clipboard.items, offsets: new Map() },
        target,
      );
      setClipboard(null);
    },
    [clipboard, evaluate, performDrop],
  );

  // --- Uploads from the computer ------------------------------------------------------
  const uploadInto = useCallback<DesktopContextValue['uploadInto']>(
    (container, files, cell) => {
      if (!canWrite) return;
      const folderId = folderIdOfContainer(container);
      const tooBig = files.filter((f) => f.size > MAX_UPLOAD_BYTES);
      const ok = files.filter((f) => f.size <= MAX_UPLOAD_BYTES);
      setNotice({
        tone: 'info',
        message: `Uploading ${ok.length} ${ok.length === 1 ? 'file' : 'files'}…`,
      });
      void (async () => {
        const results = await runBounded(ok, UPLOAD_CONCURRENCY, (file) =>
          upload.mutateAsync({
            holderId,
            file,
            // The same defaults as Browse's uploader.
            sensitivity: holder?.holderType === 'org' ? 'confidential' : 'standard',
            folderId,
          }),
        );
        const uploaded = results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
        const failures = [
          ...tooBig.map((f) => `“${f.name}”: File exceeds 100 MB upload limit.`),
          ...results.flatMap((r, i) =>
            r.status === 'rejected'
              ? [
                  `“${ok[i].name}”: ${friendlyUploadError(
                    r.reason instanceof Error ? r.reason.message : String(r.reason),
                  )}`,
                ]
              : [],
          ),
        ];
        if (cell && uploaded.length > 0) {
          arrange(
            container,
            placeAt(
              container,
              uploaded.map((doc, i) => ({ key: doc.documentId, offset: { dc: i, dr: 0 } })),
              cell,
            ),
          );
        }
        setNotice(
          failures.length > 0
            ? {
                tone: 'error',
                message: `Uploaded ${uploaded.length} of ${files.length}.`,
                details: failures,
              }
            : {
                tone: 'info',
                message: `Uploaded ${uploaded.length} ${uploaded.length === 1 ? 'file' : 'files'}.`,
              },
        );
      })();
    },
    [arrange, canWrite, holder?.holderType, holderId, placeAt, upload],
  );

  // --- Rename -------------------------------------------------------------------
  const commitRename = useCallback<DesktopContextValue['commitRename']>(
    (item, name) => {
      setRenaming(null);
      if (item.kind === 'file') {
        void fileActions.actionsFor(item.doc).rename?.(name);
      } else if (item.kind === 'folder') {
        renameFolder.mutate(
          { folderId: item.key, name },
          {
            onError: (err) =>
              setNotice({ tone: 'error', message: friendlyFolderError(err, 'rename') }),
          },
        );
      }
    },
    [fileActions, renameFolder],
  );

  const newFolder = useCallback(
    (container: string, cell: Cell | null) => {
      const parentFolderId = folderIdOfContainer(container);
      const siblings = folders.filter((f) => (f.parentFolderId ?? null) === parentFolderId);
      createFolder.mutate(
        { holderId, parentFolderId, name: freshFolderName(siblings) },
        {
          onSuccess: ({ folder }) => {
            if (cell)
              arrange(
                container,
                placeAt(container, [{ key: folder.folderId, offset: { dc: 0, dr: 0 } }], cell),
              );
            setRenaming({ container, key: folder.folderId });
          },
          onError: (err) =>
            setNotice({ tone: 'error', message: friendlyFolderError(err, 'create') }),
        },
      );
    },
    [arrange, createFolder, folders, holderId, placeAt],
  );

  // --- Menus ------------------------------------------------------------------------
  const itemsIn = useCallback((canvas: string) => registry.current.get(canvas)?.items ?? [], []);
  const selectedIn = useCallback(
    (canvas: string) => {
      const info = registry.current.get(canvas);
      return info && selection.container === info.container
        ? info.items.filter((i) => selection.keys.has(i.key))
        : [];
    },
    [selection],
  );
  const menuActions: MenuActions = {
    style,
    canWrite,
    foldersById,
    fileActions,
    hasClipboard: !!clipboard,
    trashFull,
    itemsIn,
    selectedIn,
    isFree: (canvas) => registry.current.get(canvas)?.free ?? canvas === ROOT_CONTAINER,
    open: openItem,
    preview: (doc) => preview(doc),
    openInApp,
    openerFor: openers.openerFor,
    rename: (container, key) => setRenaming({ container, key }),
    cut,
    paste,
    trash,
    newFolder,
    upload: (container, cell) => {
      uploadTarget.current = { container, cell };
      fileInputRef.current?.click();
    },
    selectAll: (canvas) => {
      const info = registry.current.get(canvas);
      if (info)
        select(
          info.container,
          info.items.map((i) => i.key),
          'replace',
        );
    },
    tidy,
    resetArrangement,
    viewOf,
    setView,
    desktopView,
    setDesktopView: (view) => setView(DESKTOP_VIEW_KEY, view),
    sortDesktop,
    emptyTrash: () => setEmptyTrash({ count: trashList.total }),
    openSettings: () => setSettingsOpen(true),
  };

  // --- Where the person is (the focused window, else the desktop) ----------------
  const focusedId = win.focusedWindow && windows[win.focusedWindow] ? win.focusedWindow : undefined;
  const focusContainer = focusedId ? windows[focusedId].folder : ROOT_CONTAINER;
  const parentOf = (container: string): string | null =>
    container === ROOT_CONTAINER || container === TRASH_KEY
      ? null
      : (foldersById.get(container)?.parentFolderId ?? ROOT_CONTAINER);
  const goTo = (container: string) => {
    if (!wide) setNarrowContainer(container);
    else if (focusedId) navigateWindow(focusedId, container);
    else win.openFolder(container);
  };

  // Keyboard, desktop-wide. Ctrl/Cmd+` cycles the open windows; Ctrl/Cmd+V in
  // a window (its title bar included) or on the desktop pastes there; a
  // canvas that has focus handles its own keys first. Shortcuts a browser
  // keeps for itself (new private window, tab switching, history) are not
  // bound — the menus carry those actions instead.
  const onDesktopKeyDown = (e: React.KeyboardEvent) => {
    if (e.defaultPrevented || !(e.ctrlKey || e.metaKey)) return;
    if (e.key === 'v' || e.key === 'V') {
      const target = e.target as HTMLElement;
      if (target.closest('input, textarea, [contenteditable="true"]')) return;
      // Where focus is: a canvas, else a window, else the container a narrow
      // screen is showing, else the desktop.
      const canvas = target
        .closest<HTMLElement>('[data-desktop-drop^="container:"]')
        ?.dataset.desktopDrop?.slice('container:'.length);
      const windowFolder =
        target.closest<HTMLElement>('[data-desktop-folder]')?.dataset.desktopFolder;
      const into =
        canvas ??
        windowFolder ??
        (!wide && narrowContainer !== TRASH_KEY ? narrowContainer : ROOT_CONTAINER);
      if (!into || into === TRASH_KEY) return;
      e.preventDefault();
      paste(into);
      return;
    }
    if (e.key === ',' && mac) {
      e.preventDefault();
      setSettingsOpen(true);
      return;
    }
    if (e.key !== '`') return;
    const open = win.stack.filter((w) => !w.min).map((w) => w.id);
    if (open.length === 0) return;
    e.preventDefault();
    const i = win.focusedWindow ? open.indexOf(win.focusedWindow) : -1;
    const next = open[(i + 1) % open.length];
    win.raise(next);
    win.requestFocus(next);
  };

  const contextValue = useMemo<DesktopContextValue>(
    () => ({
      holderId,
      style,
      viewOf,
      desktopView,
      setView,
      folders,
      foldersById,
      layout,
      fileActions,
      canWrite,
      ready: foldersQuery.isSuccess && layout.ready,
      trashFull,
      selection,
      select,
      pending,
      clipboard,
      drag: { session: drag.session, begin: drag.begin },
      renaming,
      startRename: (container, key) => setRenaming({ container, key }),
      commitRename,
      cancelRename: () => setRenaming(null),
      openItem,
      quickLook,
      openMenu: setMenu,
      arrange,
      forget,
      registerCanvas,
      tidyVersion,
      cut,
      paste,
      trash,
      uploadInto,
      performDrop,
    }),
    [
      holderId,
      style,
      viewOf,
      desktopView,
      setView,
      folders,
      foldersById,
      layout,
      fileActions,
      canWrite,
      foldersQuery.isSuccess,
      trashFull,
      selection,
      select,
      pending,
      clipboard,
      drag.session,
      drag.begin,
      renaming,
      commitRename,
      openItem,
      quickLook,
      arrange,
      forget,
      registerCanvas,
      tidyVersion,
      cut,
      paste,
      trash,
      uploadInto,
      performDrop,
    ],
  );

  // --- The workspace's handle (Fias AI's on-screen actions use it) ----------------
  useImperativeHandle(
    handleRef,
    (): DesktopHandle => ({
      openFolder: (container, options) => {
        if (!exists(container)) return;
        if (!wide) setNarrowContainer(container);
        else win.openFolder(container, { newWindow: options?.newWindow });
      },
      openFile: async (documentId) => {
        const { document } = await api.getVaultDocument(documentId);
        if (document.holderId !== holderId) return;
        openFile(document);
      },
      reveal: async ({ documentIds = [], folderIds = [] }) => {
        // Show where they are: the container of the first, opened (the
        // desktop needs no window), with them selected there.
        let container: string | null = null;
        const keys: string[] = [];
        for (const id of folderIds) {
          const folder = foldersById.get(id);
          if (!folder) continue;
          const at = containerOfFolderId(folder.parentFolderId ?? null);
          container ??= at;
          if (at === container) keys.push(id);
        }
        for (const id of documentIds) {
          const { document } = await api.getVaultDocument(id);
          if (document.holderId !== holderId || document.deletedAt) continue;
          const at = containerOfFolderId(document.folderId ?? null);
          container ??= at;
          if (at === container) keys.push(id);
        }
        if (!container) return;
        if (container !== ROOT_CONTAINER) {
          if (wide) win.openFolder(container);
          else setNarrowContainer(container);
        }
        select(container, keys, 'replace');
      },
      setFolderView: (container, patch) => {
        if (!exists(container)) return;
        setView(container, { ...viewOf(container), ...patch });
      },
      snapshot: () => ({
        openFolderId: wide ? (focusedId ? windows[focusedId].folder : null) : narrowContainer,
        openFolderIds: win.folderWindows.map(([, g]) => g.folder).slice(0, 20),
        selectedDocumentIds: [...selection.keys].filter((k) => k.startsWith('vdoc_')).slice(0, 50),
        selectedFolderIds: [...selection.keys].filter((k) => k.startsWith('vdf_')).slice(0, 50),
        viewingDocumentId:
          viewerDoc?.documentId ??
          (win.focusedWindow?.startsWith('viewer_') ? win.focusedWindow.slice(7) : null),
      }),
    }),
    [
      exists,
      focusedId,
      foldersById,
      holderId,
      narrowContainer,
      openFile,
      select,
      selection.keys,
      setView,
      viewOf,
      viewerDoc,
      wide,
      win,
      windows,
    ],
  );

  // --- What to draw -------------------------------------------------------------
  const titleOf = (container: string) =>
    container === TRASH_KEY
      ? 'Trash'
      : container === ROOT_CONTAINER
        ? 'Documents'
        : (foldersById.get(container)?.name ?? 'Folder');
  const pathOf = (container: string): PathPart[] => {
    if (container === TRASH_KEY) return [{ container: TRASH_KEY, name: 'Trash' }];
    const chain: PathPart[] = [];
    let cursor = container === ROOT_CONTAINER ? undefined : foldersById.get(container);
    for (let guard = 0; cursor && guard < 64; guard += 1) {
      chain.unshift({ container: cursor.folderId, name: cursor.name });
      cursor = cursor.parentFolderId ? foldersById.get(cursor.parentFolderId) : undefined;
    }
    return [{ container: ROOT_CONTAINER, name: 'Documents' }, ...chain];
  };
  const taskbarWindows: TaskbarWindow[] = win.stack.map((w) => {
    if (w.kind === 'viewer') {
      const viewer = win.viewers.find((v) => v.id === w.id)!;
      const Icon = iconForMime(viewer.doc.mimeType);
      return {
        id: w.id,
        title: viewer.doc.name,
        icon: <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />,
        minimized: w.min,
        focused: win.focusedWindow === w.id,
      };
    }
    const folder = windows[w.id].folder;
    return {
      id: w.id,
      title: titleOf(folder),
      icon: windowIcon(folder, foldersById.get(folder)),
      minimized: w.min,
      focused: win.focusedWindow === w.id,
    };
  });
  const selectionInfo = [...registry.current.values()].find(
    (info) => info.container === selection.container,
  );
  const selectionText =
    selection.keys.size > 0
      ? `${selection.keys.size} selected${
          selectionInfo && selectionInfo.loadedFiles < selectionInfo.totalFiles
            ? ` of ${selectionInfo.items.length} loaded`
            : ''
        }`
      : null;

  const onTaskbarWindow = (id: string) => {
    const w = win.stack.find((s) => s.id === id);
    if (!w) return;
    if (w.min) {
      win.raise(id, { min: false });
      win.requestFocus(id);
    } else if (win.focusedWindow === id) {
      win.minimize(id);
    } else {
      win.raise(id);
      win.requestFocus(id);
    }
  };

  /** A Mac window's sidebar: Favorites and the Trash. */
  const sidebarFor = (windowId: string, container: string) => {
    const archeSaves = folders.find((f) => f.path === '/Arche Saves');
    const myFias = folders.find((f) => f.path === '/My-Fias');
    const inArea = (root?: VaultDocumentFolderDto) =>
      !!root &&
      (container === root.folderId || pathOf(container).some((p) => p.container === root.folderId));
    return (
      <>
        <SidebarHeading>Favorites</SidebarHeading>
        <SidebarItem
          icon={<HardDrive className="h-4 w-4" />}
          label="Documents"
          active={container === ROOT_CONTAINER}
          onClick={() => navigateWindow(windowId, ROOT_CONTAINER)}
          drop="container:root"
        />
        {archeSaves && (
          <SidebarItem
            icon={<PackageOpen className="h-4 w-4" />}
            label="Arche Saves"
            active={inArea(archeSaves)}
            onClick={() => navigateWindow(windowId, archeSaves.folderId)}
            drop={`folder:${archeSaves.folderId}`}
          />
        )}
        {myFias && (
          <SidebarItem
            icon={<Settings2 className="h-4 w-4" />}
            label="My-Fias"
            active={inArea(myFias)}
            onClick={() => navigateWindow(windowId, myFias.folderId)}
            drop={`folder:${myFias.folderId}`}
          />
        )}
        <SidebarHeading>Locations</SidebarHeading>
        <SidebarItem
          icon={<Trash2 className="h-4 w-4" />}
          label="Trash"
          active={container === TRASH_KEY}
          onClick={() => navigateWindow(windowId, TRASH_KEY)}
          drop="trash"
        />
      </>
    );
  };

  const folderWindowContent = (windowId: string, container: string) => {
    if (container === TRASH_KEY) {
      return (
        <TrashWindowContent
          holderId={holderId}
          fileActions={fileActions}
          canWrite={canWrite}
          onOpenDocument={(doc) => preview(doc)}
          onEmptyTrash={(count) => setEmptyTrash({ count })}
        />
      );
    }
    const view = viewOf(container);
    const area = areaOfContainer(container, foldersById);
    const up = parentOf(container);
    const drawn = effectiveMode(view.mode, style);
    return (
      <div className="flex h-full flex-col">
        {area !== 'documents' && (
          <p className="shrink-0 border-b border-border bg-muted/30 px-3 py-1.5 text-xs text-muted-foreground">
            {areaNoteFor(foldersById.get(container), area)}
          </p>
        )}
        <div className="min-h-0 flex-1">
          {drawn === 'columns' ? (
            <ColumnsView
              container={container}
              view={view}
              windowId={windowId}
              onNavigate={(next) => navigateWindow(windowId, next)}
            />
          ) : drawn === 'gallery' ? (
            <GalleryView container={container} view={view} windowId={windowId} />
          ) : (
            <DesktopCanvas
              container={container}
              windowId={windowId}
              view={view}
              onSort={(sort) => setView(container, chooseSort(view, sort))}
              label={`Files in ${titleOf(container)}`}
              onUp={up ? () => navigateWindow(windowId, up) : undefined}
            />
          )}
        </div>
      </div>
    );
  };

  const viewerToolbar = (doc: VaultDocumentDto) => {
    const opener = openers.openerFor(doc);
    const actions = fileActions.actionsFor(doc);
    const button = cn(
      'flex h-7 shrink-0 items-center gap-1.5 rounded px-2 text-xs',
      mac
        ? 'text-foreground/80 hover:bg-black/5 dark:hover:bg-white/10'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
    );
    return (
      <div className={cn('flex min-w-0 flex-1 items-center gap-1', !mac && 'justify-end')}>
        {mac && (
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground/85">
            {doc.name}
          </span>
        )}
        {opener.apps.map((app) => (
          <button
            key={app.archeId}
            type="button"
            className={button}
            onClick={() => openInApp(doc, app)}
          >
            {mac ? `Open with ${app.label}` : `Open in ${app.label}`}
          </button>
        ))}
        <button type="button" className={button} onClick={actions.download} aria-label="Download">
          <Download className="h-3.5 w-3.5" />
          {!mac && 'Download'}
        </button>
        <button
          type="button"
          className={button}
          onClick={actions.viewDetails}
          aria-label={mac ? 'Get Info' : 'Details'}
        >
          <Info className="h-3.5 w-3.5" />
          {!mac && 'Details'}
        </button>
      </div>
    );
  };

  const menuBar = mac
    ? macMenuBar(menuActions, {
        container: focusContainer,
        windowId: focusedId,
        canBack: !!focusedId && win.canStep(focusedId, 'back'),
        canForward: !!focusedId && win.canStep(focusedId, 'forward'),
        onBack: focusedId ? () => win.back(focusedId) : undefined,
        onForward: focusedId ? () => win.forward(focusedId) : undefined,
        onUp:
          focusedId && parentOf(focusContainer)
            ? () => navigateWindow(focusedId, parentOf(focusContainer)!)
            : undefined,
        onClose: win.focusedWindow ? () => win.close(win.focusedWindow!) : undefined,
        onMinimize: win.focusedWindow ? () => win.minimize(win.focusedWindow!) : undefined,
        onZoom: win.focusedWindow ? () => win.toggleZoom(win.focusedWindow!) : undefined,
        goTo,
        windows: taskbarWindows.map((w) => ({
          id: w.id,
          title: w.title,
          focused: w.focused,
          onSelect: () => onTaskbarWindow(w.id),
        })),
      })
    : [];

  const session = drag.session;
  const trashHover =
    session?.target?.kind === 'trash' ? (session.allowed ? 'allowed' : 'refused') : null;
  const saveStatus =
    layout.status === 'saving' || layout.status === 'pending'
      ? 'Saving…'
      : layout.status === 'retrying'
        ? 'Not saved yet'
        : layout.status === 'limit'
          ? 'Arrangement full'
          : null;

  return (
    <DesktopContext.Provider value={contextValue}>
      <div className="flex min-h-0 flex-1 flex-col" onKeyDown={onDesktopKeyDown}>
        {(placement.ui || fileActions.ui || notice) && (
          <div className="shrink-0 px-3 pt-2">
            {placement.ui}
            {fileActions.ui}
            {notice && <NoticeBanner notice={notice} onDismiss={() => setNotice(null)} />}
          </div>
        )}

        {wide && mac && <MacMenuBar menus={menuBar} status={saveStatus} />}

        {wide ? (
          <div
            ref={areaRef}
            className={cn(
              'relative min-h-[28rem] flex-1 overflow-hidden',
              mac
                ? 'bg-gradient-to-br from-sky-100 via-indigo-100 to-rose-100 dark:from-slate-800 dark:via-indigo-950 dark:to-slate-900'
                : 'bg-muted/20',
            )}
          >
            <div className={cn('absolute inset-0', mac && 'bottom-16')}>
              <DesktopCanvas container={ROOT_CONTAINER} label="Desktop" emptyMessage="" />
            </div>
            {win.stack
              .filter((w) => !w.min)
              .map((w, rank) => {
                if (w.kind === 'viewer') {
                  const viewer = win.viewers.find((v) => v.id === w.id)!;
                  const Icon = iconForMime(viewer.doc.mimeType);
                  return (
                    <FolderWindow
                      key={w.id}
                      id={w.id}
                      style={style}
                      title={viewer.doc.name}
                      icon={<Icon className="h-4 w-4 text-muted-foreground" aria-hidden />}
                      geometry={viewer.geometry}
                      bounds={areaSize}
                      zIndex={10 + rank}
                      focused={win.focusedWindow === w.id}
                      focusRequest={win.focusRequests[w.id] ?? 0}
                      onFocus={() => win.raise(w.id)}
                      onCommit={(next) => win.commit(w.id, next)}
                      onMinimize={() => win.minimize(w.id)}
                      onClose={() => win.close(w.id)}
                      onZoom={() => win.toggleZoom(w.id)}
                      toolbar={viewerToolbar(viewer.doc)}
                    >
                      <FilePreviewPane
                        doc={viewer.doc}
                        preview={openers.openerFor(viewer.doc).preview}
                      />
                    </FolderWindow>
                  );
                }
                const g = windows[w.id];
                const container = g.folder;
                const view = viewOf(container);
                const up = parentOf(container);
                const info = registry.current.get(w.id);
                const selectedHere = selection.container === container ? selection.keys.size : 0;
                return (
                  <FolderWindow
                    key={w.id}
                    id={w.id}
                    style={style}
                    folder={container}
                    title={titleOf(container)}
                    icon={windowIcon(container, foldersById.get(container))}
                    geometry={g}
                    bounds={areaSize}
                    zIndex={10 + rank}
                    focused={win.focusedWindow === w.id}
                    focusRequest={win.focusRequests[w.id] ?? 0}
                    onFocus={() => win.raise(w.id)}
                    onCommit={(next) => win.commit(w.id, next)}
                    onMinimize={() => win.minimize(w.id)}
                    onClose={() => win.close(w.id)}
                    onZoom={() => win.toggleZoom(w.id)}
                    toolbar={
                      <WindowToolbar
                        style={style}
                        title={titleOf(container)}
                        path={pathOf(container)}
                        view={view}
                        onView={(next) => setView(container, next)}
                        canBack={win.canStep(w.id, 'back')}
                        canForward={win.canStep(w.id, 'forward')}
                        onBack={() => win.back(w.id)}
                        onForward={() => win.forward(w.id)}
                        onUp={up ? () => navigateWindow(w.id, up) : undefined}
                        onNavigate={(next) => navigateWindow(w.id, next)}
                        trash={container === TRASH_KEY}
                      />
                    }
                    sidebar={mac ? sidebarFor(w.id, container) : undefined}
                    statusBar={
                      container === TRASH_KEY ? undefined : (
                        <WindowStatusBar
                          style={style}
                          itemCount={info?.items.length ?? 0}
                          totalCount={
                            info && info.totalFiles > info.loadedFiles
                              ? info.items.length - info.loadedFiles + info.totalFiles
                              : undefined
                          }
                          selectedCount={selectedHere}
                          view={view}
                          onView={(next) => setView(container, next)}
                        />
                      )
                    }
                  >
                    {folderWindowContent(w.id, container)}
                  </FolderWindow>
                );
              })}
            {mac && (
              <MacDock
                windows={taskbarWindows}
                onWindow={onTaskbarWindow}
                onFinder={() => win.openFolder(ROOT_CONTAINER)}
                trashFull={trashFull}
                trashHover={trashHover}
                onTrash={() => win.openFolder(TRASH_KEY)}
                onTrashMenu={(pointer, el) =>
                  setMenu({
                    ...pointer,
                    container: ROOT_CONTAINER,
                    item: { kind: 'trash', key: TRASH_KEY, name: 'Trash', full: trashFull },
                    returnFocus: el,
                  })
                }
              />
            )}
          </div>
        ) : (
          <NarrowDesktop
            container={narrowContainer}
            foldersById={foldersById}
            onNavigate={setNarrowContainer}
            trash={folderWindowContent('narrow', TRASH_KEY)}
            canvas={(container) => {
              const area = areaOfContainer(container, foldersById);
              const view = container === ROOT_CONTAINER ? undefined : viewOf(container);
              const up = parentOf(container);
              return (
                <div className="flex h-full flex-col">
                  {area !== 'documents' && (
                    <p className="shrink-0 border-b border-border bg-muted/30 px-3 py-1.5 text-xs text-muted-foreground">
                      {areaNoteFor(foldersById.get(container), area)}
                    </p>
                  )}
                  <div className="min-h-0 flex-1">
                    <DesktopCanvas
                      container={container}
                      // A narrow screen draws icons and lists, never Columns / Gallery.
                      view={
                        view && (view.mode === 'columns' || view.mode === 'gallery')
                          ? { ...view, mode: 'details' }
                          : view
                      }
                      onSort={
                        view ? (sort) => setView(container, chooseSort(view, sort)) : undefined
                      }
                      label={
                        container === ROOT_CONTAINER ? 'Desktop' : `Files in ${titleOf(container)}`
                      }
                      onUp={up ? () => setNarrowContainer(up) : undefined}
                      emptyMessage={container === ROOT_CONTAINER ? '' : undefined}
                    />
                  </div>
                </div>
              );
            }}
          />
        )}

        {wide && !mac && (
          <DesktopTaskbar
            windows={taskbarWindows}
            onWindow={onTaskbarWindow}
            selectionText={selectionText}
            status={layout.status}
            loadFailed={layout.loadFailed}
            onResetArrangement={resetArrangement}
            onSettings={() => setSettingsOpen(true)}
          />
        )}
      </div>

      {/* The drag ghost follows the pointer; it takes no pointer events, so
          hit-testing sees what's underneath. It alone re-renders per frame. */}
      {session && <DragGhost session={session} pointer={drag.pointer} />}

      {menu && (
        <DesktopContextMenu
          x={menu.x}
          y={menu.y}
          style={style}
          label={menu.item ? `Actions for ${menu.item.name}` : 'Desktop actions'}
          entries={contextMenuEntries(menuActions, menu)}
          onClose={() => setMenu(null)}
          returnFocus={menu.returnFocus}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        aria-hidden
        tabIndex={-1}
        onChange={(e) => {
          const files = e.target.files ? Array.from(e.target.files) : [];
          const target = uploadTarget.current;
          e.target.value = '';
          if (files.length > 0 && target) uploadInto(target.container, files, target.cell);
        }}
      />

      <FileViewer
        document={viewerDoc}
        onClose={() => setViewerDoc(null)}
        onDownload={(doc) => void downloadVaultDocument(doc)}
      />

      <DesktopSettingsDialog
        open={settingsOpen}
        style={style}
        onStyle={(next: DesktopStyle) => setStyle(next)}
        onClose={() => setSettingsOpen(false)}
      />

      <DeleteFolderDialog
        open={deletingFolder !== null}
        folderName={deletingFolder?.name ?? ''}
        deletePending={deleteFolder.isPending}
        deleteError={deleteFolderError}
        onCancel={() => setDeletingFolder(null)}
        onDelete={() => {
          if (!deletingFolder) return;
          setDeleteFolderError(null);
          deleteFolder.mutate(
            { folderId: deletingFolder.folderId, holderId },
            {
              onSuccess: () => {
                const id = deletingFolder.folderId;
                setDeletingFolder(null);
                // Close every window showing it (its contents went with it).
                for (const [windowId, g] of win.folderWindows) {
                  if (g.folder === id) win.setWindow(windowId, null);
                }
              },
              onError: (err) => setDeleteFolderError(friendlyFolderError(err, 'delete')),
            },
          );
        }}
      />

      <EmptyTrashDialog
        open={emptyTrash !== null}
        holderId={holderId}
        trashCount={emptyTrash?.count ?? 0}
        onClose={() => setEmptyTrash(null)}
      />
    </DesktopContext.Provider>
  );
}
